import { LOWEST_PITCH } from '../../gregorio-chant/constants';
import { GregorioGlyphType } from '../../gregorio-chant/enums/GregorioGlyphType';
import { GregorioLiquescentia } from '../../gregorio-chant/enums/GregorioLiquescentia';
import { GregorioShape } from '../../gregorio-chant/enums/GregorioShape';
import { GregorioType } from '../../gregorio-chant/enums/GregorioType';
import { is_initio_debilis, is_fused, is_tail_liquescentia } from '../../gregorio-chant/score_parsing/other';
import { GregorioGlyph } from '../../gregorio-chant/structures/GregorioGlyph';
import { GregorioNoteElement } from '../../gregorio-chant/structures/GregorioNoteElement';
import { GtexType } from '../episemas/enums';

export enum gtex_alignment {
  AT_ONE_NOTE = 0,
  AT_FLEXUS = 1,
  AT_PORRECTUS = 2,
  AT_INITIO_DEBILIS = 3,
  AT_QUILISMA = 4,
  AT_ORISCUS = 5,
  AT_PUNCTUM_INCLINATUM = 6,
  AT_STROPHA = 7,
  AT_FLEXUS_1 = 8,
  AT_FLEXUS_DEMINUTUS = 9,
  AT_VIRGULA = 10,
  AT_DIVISIO_MINIMA = 11,
  AT_DIVISIO_MINOR = 12,
  AT_DIVISIO_MAIOR = 13,
  AT_DIVISIO_DOMINICAN = 14,
  AT_VIRGULA_PAREN = 15,
  AT_DIVISIO_MINIMA_PAREN = 16,
  AT_DIVISIO_FINALIS = 17,
  AT_EMPTY_SYLLABLE = 18
} gtex_alignment;

export function gregoriotex_determine_glyph_name(
  glyphs: GregorioGlyph[],
  glyph_index: number,
): [string, gtex_alignment, GtexType] {
  let shape: SHAPE | string | null = null;
  let type: gtex_alignment | null = null;
  let ltype: gtex_glyph_liquescentia;
  let ambitus: number;
  let second_note: GregorioNoteElement;
  let gtype: GtexType;
  const glyph: GregorioGlyph = glyphs[glyph_index];
  // gregorio_assert(glyph, gregoriotex_determine_glyph_name,
  //   "called with NULL pointer", return "");
  // gregorio_assert(glyph -> u.notes.first_note, gregoriotex_determine_glyph_name,
  //   "called with a glyph that has no note", return "");
  gtype = GtexType.T_ONE_NOTE;
  switch (glyph.notes.glyph_type) {
    case GregorioGlyphType.PODATUS:
      switch (glyph.notes.notes[0].shape) {
        case GregorioShape.QUILISMA:
          type = gtex_alignment.AT_QUILISMA;
          /* the next if is because we made the choice that AUCTUS shapes
          look like pes quadratum. */
          if (glyph.notes.liquescentia &
            (GregorioLiquescentia.AUCTUS_ASCENDENS | GregorioLiquescentia.AUCTUS_DESCENDENS)) {
            gtype = GtexType.T_QUILISMA_PES_QUADRATUM;
            shape = SHAPE.QuilismaPesQuadratum;
          } else {
            gtype = GtexType.T_QUILISMA_PES;
            shape = SHAPE.QuilismaPes;
          }
          ltype = gtex_glyph_liquescentia.NO_INITIO;
          break;
        case GregorioShape.ORISCUS_ASCENDENS:
        case GregorioShape.ORISCUS_SCAPUS_ASCENDENS:
          type = gtex_alignment.AT_ORISCUS;
          gtype = GtexType.T_PES_QUASSUS;
          shape = quadratum_shape(glyph, SHAPE.PesQuassus,
            SHAPE.PesQuassusLongqueue, SHAPE.PesQuassusOpenqueue);
          ltype = gtex_glyph_liquescentia.NO_INITIO;
          break;
        case GregorioShape.ORISCUS_DESCENDENS:
        case GregorioShape.ORISCUS_SCAPUS_DESCENDENS:
          type = gtex_alignment.AT_ORISCUS;
          gtype = GtexType.T_PES_QUASSUS;
          shape = quadratum_shape(glyph, SHAPE.PesQuassusInusitatus,
            SHAPE.PesQuassusInusitatusLongqueue,
            SHAPE.PesQuassusInusitatusOpenqueue);
          ltype = gtex_glyph_liquescentia.NO_INITIO;
          break;
        default:
          type = gtex_alignment.AT_ONE_NOTE;
          if (glyph.notes.liquescentia
            & (GregorioLiquescentia.AUCTUS_ASCENDENS | GregorioLiquescentia.AUCTUS_DESCENDENS)) {
            gtype = GtexType.T_PES_QUADRATUM;
            shape = SHAPE.PesQuadratum;
          } else {
            gtype = GtexType.T_PES;
            shape = SHAPE.Pes;
          }
          ltype = gtex_glyph_liquescentia.ALL;
          break;
      }
      break;
    case GregorioGlyphType.PES_QUADRATUM:
      switch (glyph.notes.notes[0].shape) {
        case GregorioShape.QUILISMA:
          type = gtex_alignment.AT_QUILISMA;
          gtype = GtexType.T_QUILISMA_PES_QUADRATUM;
          shape = quadratum_shape(glyph, SHAPE.QuilismaPesQuadratum,
            SHAPE.QuilismaPesQuadratumLongqueue,
            SHAPE.QuilismaPesQuadratumOpenqueue);
          ltype = gtex_glyph_liquescentia.NO_INITIO;
          break;
        default:
          type = gtex_alignment.AT_ONE_NOTE;
          gtype = GtexType.T_PES_QUADRATUM;
          shape = quadratum_shape(glyph, SHAPE.PesQuadratum,
            SHAPE.PesQuadratumLongqueue, SHAPE.PesQuadratumOpenqueue);
          ltype = gtex_glyph_liquescentia.ALL;
          break;
      }
      break;
    case GregorioGlyphType.PES_ASCENDENS_ORISCUS:
      type = gtex_alignment.AT_ONE_NOTE;
      gtype = GtexType.T_PES_ORISCUS;
      shape = SHAPE.PesAscendensOriscus;
      ltype = gtex_glyph_liquescentia.ALL;
      break;
    case GregorioGlyphType.PES_DESCENDENS_ORISCUS:
      type = gtex_alignment.AT_ONE_NOTE;
      gtype = GtexType.T_PES_ORISCUS;
      shape = SHAPE.PesDescendensOriscus;
      ltype = gtex_glyph_liquescentia.ALL;
      break;
    case GregorioGlyphType.FLEXA:
      ambitus = glyph.notes.notes[0].pitch - glyph.notes.notes[1].pitch;
      if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
        type = gtex_alignment.AT_FLEXUS_DEMINUTUS;
      } else {
        if (ambitus == 1) {
          type = gtex_alignment.AT_FLEXUS_1;
        } else {
          type = gtex_alignment.AT_FLEXUS;
        }
      }
      switch (glyph.notes.notes[0].shape) {
        case GregorioShape.ORISCUS_ASCENDENS:
          gtype = GtexType.T_FLEXUS_ORISCUS;
          shape = SHAPE.FlexusOriscusInusitatus;
          ltype = gtex_glyph_liquescentia.NO_INITIO;
          break;

        case GregorioShape.ORISCUS_DESCENDENS:
          gtype = GtexType.T_FLEXUS_ORISCUS;
          shape = SHAPE.FlexusOriscus;
          ltype = gtex_glyph_liquescentia.NO_INITIO;
          break;

        case GregorioShape.ORISCUS_SCAPUS_ASCENDENS:
          gtype = GtexType.T_FLEXUS_ORISCUS_SCAPUS;
          shape = flexus_shape(glyph, ambitus,
            SHAPE.FlexusOriscusScapusInusitatus,
            SHAPE.FlexusOriscusScapusInusitatusLongqueue,
            SHAPE.FlexusOriscusScapusInusitatusOpenqueue);
          ltype = gtex_glyph_liquescentia.NO_INITIO;
          break;

        case GregorioShape.ORISCUS_SCAPUS_DESCENDENS:
          gtype = GtexType.T_FLEXUS_ORISCUS_SCAPUS;
          shape = flexus_shape(glyph, ambitus, SHAPE.FlexusOriscusScapus,
            SHAPE.FlexusOriscusScapusLongqueue,
            SHAPE.FlexusOriscusScapusOpenqueue);
          ltype = gtex_glyph_liquescentia.NO_INITIO;
          break;

        default:
          gtype = glyph.notes.fuse_to_next_glyph ? GtexType.T_PORRECTUS : GtexType.T_FLEXUS;
          shape = flexus_shape(glyph, ambitus, SHAPE.Flexus,
            SHAPE.FlexusLongqueue, SHAPE.FlexusOpenqueue);
          ltype = gtex_glyph_liquescentia.NO_INITIO;
          break;
      }
      break;
    case GregorioGlyphType.TORCULUS:
      gtype = GtexType.T_TORCULUS;
      if (glyph.notes.notes[0].shape == GregorioShape.QUILISMA) {
        type = gtex_alignment.AT_QUILISMA;
        shape = SHAPE.TorculusQuilisma;
        ltype = gtex_glyph_liquescentia.NO_INITIO;
      } else {
        type = gtex_alignment.AT_ONE_NOTE;
        shape = SHAPE.Torculus;
        ltype = gtex_glyph_liquescentia.ALL;
      }
      break;
    case GregorioGlyphType.TORCULUS_LIQUESCENS:
      gtype = GtexType.T_TORCULUS_LIQUESCENS;
      if (glyph.notes.notes[0].shape == GregorioShape.QUILISMA) {
        type = gtex_alignment.AT_QUILISMA;
        shape = SHAPE.TorculusLiquescensQuilisma;
      } else {
        type = gtex_alignment.AT_ONE_NOTE;
        shape = SHAPE.TorculusLiquescens;
      }
      ltype = gtex_glyph_liquescentia.ONLY_DEMINUTUS;
      break;
    case GregorioGlyphType.TORCULUS_RESUPINUS_FLEXUS:
      type = gtex_alignment.AT_ONE_NOTE;
      gtype = GtexType.T_TORCULUS_RESUPINUS_FLEXUS;
      break;
    case GregorioGlyphType.PORRECTUS:
      type = gtex_alignment.AT_PORRECTUS;
      gtype = GtexType.T_PORRECTUS;
      shape = porrectus_shape(glyph, SHAPE.Porrectus,
        SHAPE.PorrectusLongqueue);
      ltype = gtex_glyph_liquescentia.NO_INITIO;
      break;
    case GregorioGlyphType.TORCULUS_RESUPINUS:
      gtype = GtexType.T_TORCULUS_RESUPINUS;
      if (glyph.notes.notes[0].shape == GregorioShape.QUILISMA) {
        type = gtex_alignment.AT_QUILISMA;
        shape = SHAPE.TorculusResupinusQuilisma;
      } else {
        type = gtex_alignment.AT_ONE_NOTE;
        shape = SHAPE.TorculusResupinus;
      }
      ltype = gtex_glyph_liquescentia.ALL;
      break;
    case GregorioGlyphType.PORRECTUS_FLEXUS:
      type = gtex_alignment.AT_PORRECTUS;
      gtype = GtexType.T_PORRECTUS_FLEXUS;
      shape = porrectus_shape(glyph, SHAPE.PorrectusFlexus,
        SHAPE.PorrectusFlexusLongqueue);
      ltype = gtex_glyph_liquescentia.NO_INITIO;
      break;
    case GregorioGlyphType.PORRECTUS_NO_BAR:
      type = gtex_alignment.AT_PORRECTUS;
      gtype = GtexType.T_TORCULUS_RESUPINUS;
      shape = SHAPE.PorrectusNobar;
      ltype = gtex_glyph_liquescentia.NO_INITIO;
      break;
    case GregorioGlyphType.PORRECTUS_FLEXUS_NO_BAR:
      type = gtex_alignment.AT_PORRECTUS;
      gtype = GtexType.T_TORCULUS_RESUPINUS_FLEXUS;
      shape = SHAPE.PorrectusFlexusNobar;
      ltype = gtex_glyph_liquescentia.NO_INITIO;
      break;
    case GregorioGlyphType.ANCUS:
      type = gtex_alignment.AT_ONE_NOTE;
      // gregorio_assert(glyph -> u.notes.liquescentia & L_DEMINUTUS,
      //   gregoriotex_determine_glyph_name,
      //   "encountered non-deminutus ancus", break);
      second_note = glyph.notes.notes[1];
      if (glyph.notes.notes[0].pitch - glyph.notes.notes[1].pitch == 1) {
        type = gtex_alignment.AT_FLEXUS_1;
      } else {
        type = gtex_alignment.AT_FLEXUS;
      }
      gtype = GtexType.T_ANCUS;
      ambitus = glyph.notes.notes[0].pitch - glyph.notes.notes[1].pitch;
      shape = flexus_shape(glyph, ambitus, SHAPE.Ancus,
        SHAPE.AncusLongqueue, SHAPE.Ancus);
      ltype = gtex_glyph_liquescentia.ONLY_DEMINUTUS;
      break;
    case GregorioGlyphType.SCANDICUS:
      type = gtex_alignment.AT_ONE_NOTE;
      gtype = GtexType.T_SCANDICUS;
      shape = SHAPE.Scandicus;
      ltype = gtex_glyph_liquescentia.NO_INITIO;
      break;
    case GregorioGlyphType.SALICUS:
      type = gtex_alignment.AT_ONE_NOTE;
      gtype = GtexType.T_SALICUS;
      ambitus = glyph.notes.notes[2].pitch - glyph.notes.notes[1].pitch;
      shape = flexus_shape(glyph, ambitus, SHAPE.Salicus,
        SHAPE.SalicusLongqueue, SHAPE.Salicus);
      ltype = gtex_glyph_liquescentia.NO_INITIO;
      break;
    case GregorioGlyphType.SALICUS_FLEXUS:
      type = gtex_alignment.AT_ONE_NOTE;
      gtype = GtexType.T_SALICUS_FLEXUS;
      shape = SHAPE.SalicusFlexus;
      ltype = gtex_glyph_liquescentia.NO_INITIO;
      break;
    case GregorioGlyphType.PUNCTUM_INCLINATUM:
    case GregorioGlyphType.PUNCTA_INCLINATA:
    case GregorioGlyphType._2_PUNCTA_INCLINATA_DESCENDENS:
    case GregorioGlyphType._3_PUNCTA_INCLINATA_DESCENDENS:
    case GregorioGlyphType._4_PUNCTA_INCLINATA_DESCENDENS:
    case GregorioGlyphType._5_PUNCTA_INCLINATA_DESCENDENS:
    case GregorioGlyphType._2_PUNCTA_INCLINATA_ASCENDENS:
    case GregorioGlyphType._3_PUNCTA_INCLINATA_ASCENDENS:
    case GregorioGlyphType._4_PUNCTA_INCLINATA_ASCENDENS:
    case GregorioGlyphType._5_PUNCTA_INCLINATA_ASCENDENS:
    case GregorioGlyphType.PUNCTUM:
    case GregorioGlyphType.STROPHA:
    case GregorioGlyphType.VIRGA:
    case GregorioGlyphType.VIRGA_REVERSA:
    case GregorioGlyphType.STROPHA_AUCTA:
    case GregorioGlyphType.DISTROPHA:
    case GregorioGlyphType.DISTROPHA_AUCTA:
    case GregorioGlyphType.TRISTROPHA:
    case GregorioGlyphType.TRISTROPHA_AUCTA:
    case GregorioGlyphType.BIVIRGA:
    case GregorioGlyphType.TRIVIRGA:
    case GregorioGlyphType.ALTERATION:
      type = gtex_alignment.AT_ONE_NOTE;
      break;
    default:
      /* not reachable unless there's a programming error */
      /* LCOV_EXCL_START */
      // gregorio_fail2(gregoriotex_determine_glyph_name,
      //   "called with unknown glyph: %s",
      //   gregorio_glyph_type_to_string(glyph -> u.notes.glyph_type));
      break;
    /* LCOV_EXCL_STOP */
  }

  if (shape !== null) {
    shape = compute_glyph_name(glyphs, glyph_index, shape, ltype!, glyph.isSingleNote);
  }


  /* we fix *type with initio_debilis */
  if (type == gtex_alignment.AT_ONE_NOTE) {
    if (is_initio_debilis(glyph.notes.liquescentia)) {
      type = gtex_alignment.AT_INITIO_DEBILIS;
    }
  }

  // if (shape === null) {
  //   throw new Error('Failed to find right shape');
  // }

  return [shape as string, type!, gtype];
}


export enum gtex_glyph_liquescentia {
  ALL = 0, /* for glyphs that accept all liquecentiae */
  NO_INITIO, /* for glyphs that don't accept initio debilis */
  NONE, /* for glyphs that don't accept liquescentia */
  ONLY_DEMINUTUS,
  FUSIBLE_INITIO
} gtex_glyph_liquescentia;

export function compute_glyph_name(
  glyphs: GregorioGlyph[],
  glyphIndex: number,
  orig_shape: SHAPE,
  ltype: gtex_glyph_liquescentia,
  is_single_note: boolean): string {
  const glyph = glyphs[glyphIndex];
  let liquescentia: LIQ | null = gregoriotex_determine_liquescentia(ltype, glyph);

  let shape = orig_shape;
  // let ambitus1, ambitus2, ambitus3, 
  let fuse_ambitus = 0;
  let fuse_head: FUSE | null = null;
  let fuse_tail: FUSE | null = null;
  const previous = glyphIndex > 0 ? glyphs[glyphIndex - 1] : null;
  const fuse_from_previous_note =
    (previous && previous.type == GregorioType.GLYPH) ? previous.notes.fuse_to_next_glyph : 0;

  /* then we start making our formula */
  // gregorio_assert(glyph, compute_glyph_name, "called with NULL pointer",
  //     return "");
  // gregorio_assert(glyph -> u.notes.first_note, compute_glyph_name,
  //   "called with a glyph that have no note", return "");

  const fuse_to_next_note = glyph.notes.fuse_to_next_glyph;

  switch (glyph.notes.glyph_type) {
    case GregorioGlyphType.PODATUS:
    case GregorioGlyphType.PUNCTUM:
    case GregorioGlyphType.FLEXA:
      /* directionally head-fusible */
      if (fuse_from_previous_note < 0) {
        if (glyph.notes.notes[0].shape !== GregorioShape.QUILISMA &&
          glyph.notes.notes[0].shape !== GregorioShape.QUILISMA_QUADRATUM) {
          if (fuse_from_previous_note < -1) {
            fuse_head = FUSE.Lower;
          } else if (glyph.notes.notes[0].shape == GregorioShape.ORISCUS_DESCENDENS ||
            glyph.notes.notes[0].shape == GregorioShape.ORISCUS_SCAPUS_DESCENDENS) {
            /* fuse_from_previous_note will be -1 here */
            fuse_head = FUSE.LowerOblatus;
          }
        }
      } else if (fuse_from_previous_note > 0) {
        if (fuse_from_previous_note > 1) {
          fuse_head = FUSE.Upper;
        } else if (glyph.notes.notes[0].shape == GregorioShape.ORISCUS_ASCENDENS ||
          glyph.notes.notes[0].shape == GregorioShape.ORISCUS_SCAPUS_ASCENDENS) {
          /* fuse_from_previous_note will be 1 here */
          fuse_head = FUSE.UpperOblatus;
        }
      }
      break;

    default:
      /* not directionally head-fusible */
      break;
  }

  switch (glyph.notes.glyph_type) {
    case GregorioGlyphType.FLEXA:
      if (fuse_to_next_note <= 0) {
        /* a flexa is only fusible up */
        break;
      }
    /* else fall through */
    case GregorioGlyphType.VIRGA_REVERSA:
    case GregorioGlyphType.PUNCTUM:
      /* tail-fusible */
      if (fuse_to_next_note < 0) {
        fuse_tail = FUSE.Down;
        fuse_ambitus = -fuse_to_next_note;
      } else if (fuse_to_next_note > 0) {
        fuse_tail = FUSE.Up;
        fuse_ambitus = fuse_to_next_note;
      }

      if (fuse_tail && liquescentia == LIQ.Nothing) {
        liquescentia = null;
      }
      break;

    default:
      /* not tail-fusible */
      break;
  }

  if (is_fused(glyph.notes.liquescentia)) {
    if (shape == SHAPE.AscendensOriscusScapus
      || shape == SHAPE.AscendensOriscusScapusLongqueue
      || shape == SHAPE.AscendensOriscusScapusOpenqueue) {
      shape = SHAPE.AscendensOriscus;
    } else if (shape == SHAPE.DescendensOriscusScapus
      || shape == SHAPE.DescendensOriscusScapusLongqueue
      || shape == SHAPE.DescendensOriscusScapusOpenqueue) {
      shape = SHAPE.DescendensOriscus;
    } else if (shape == SHAPE.FlexusOriscusScapus
      || shape == SHAPE.FlexusOriscusScapusLongqueue
      || shape == SHAPE.FlexusOriscusScapusOpenqueue) {
      shape = SHAPE.FlexusOriscus;
    } else if (shape == SHAPE.FlexusOriscusScapusInusitatus
      || shape == SHAPE.FlexusOriscusScapusInusitatusLongqueue
      || shape == SHAPE.FlexusOriscusScapusInusitatusOpenqueue) {
      shape = SHAPE.FlexusOriscusInusitatus;
    }
  }

  let current_note_index = 0;

  if (is_single_note) {
    if (liquescentia == LIQ.Nothing) {
      liquescentia = null;
    }
    if (!(fuse_tail) && fuse_head != FUSE.UpperOblatus
      && fuse_head != FUSE.LowerOblatus) {
      /* single note fused shapes have weird names */
      if (fuse_head == FUSE.Upper) {
        if (shape == SHAPE.Punctum) {
          shape = SHAPE.PunctumLineBL;
        } else if (shape == SHAPE.AscendensOriscus) {
          shape = SHAPE.AscendensOriscusLineBL;
        } else if (shape == SHAPE.DescendensOriscus) {
          shape = SHAPE.DescendensOriscusLineBL;
        }
      } else if (fuse_head == FUSE.Lower) {
        if (shape == SHAPE.Punctum) {
          shape = SHAPE.PunctumLineTL;
        } else if (shape == SHAPE.AscendensOriscus) {
          shape = SHAPE.AscendensOriscusLineTL;
        } else if (shape == SHAPE.DescendensOriscus) {
          shape = SHAPE.DescendensOriscusLineTL;
        }
      }
      fuse_head = null;
    }

    // eslint-disable-next-line max-len
    return `${fuse_head && FUSE[fuse_head] || ''}${SHAPE[shape]}${tex_ambitus[fuse_ambitus]}${liquescentia != null && LIQ[liquescentia] || ''}${fuse_tail != null && FUSE[fuse_tail] || ''}`;
  }
  // gregorio_assert(current_note -> next, compute_glyph_name,
  //   "called with a multi-note glyph that has only one note", return "");
  // gregorio_assert((ambitus1 = compute_ambitus(current_note)),
  //   compute_glyph_name, "unexpected unison on multi-note glyph",
  //     return "");

  const ambitus1 = compute_ambitus(glyph.notes.notes, current_note_index);

  if (is_fused(glyph.notes.liquescentia)) {
    if (shape == SHAPE.Flexus || shape == SHAPE.FlexusLongqueue
      || shape == SHAPE.FlexusOpenqueue) {
      if (fuse_to_next_note) {
        fuse_head = null;
      }
      if (fuse_head) {
        shape = SHAPE.Flexus;
      } else {
        shape = SHAPE.FlexusNobar;
      }
    } else if (shape == SHAPE.Porrectus
      || shape == SHAPE.PorrectusLongqueue) {
      shape = SHAPE.PorrectusNobar;
    }
  } else {
    if (fuse_to_next_note && (shape == SHAPE.FlexusLongqueue
      || shape == SHAPE.FlexusOpenqueue)) {
      /* a porrectus-like flexus has no longqueue variant */
      shape = SHAPE.Flexus;
    }
  }
  if (shape == SHAPE.SalicusLongqueue && liquescentia != LIQ.Nothing) {
    /* the salicus queue is at the end of the glyph, and it doesn't exist
    for the liquescent forms */
    shape = SHAPE.Salicus;
  }

  if (glyph.notes.notes.length == 2) {
    // eslint-disable-next-line max-len
    const name = `${fuse_head && FUSE[fuse_head] || ''}${SHAPE[shape]}${tex_ambitus[ambitus1]}${tex_ambitus[fuse_ambitus]}${liquescentia != null && LIQ[liquescentia] || ''}${fuse_tail != null && FUSE[fuse_tail] || ''}`;
    return name;
  }

  current_note_index++;
  const ambitus2 = compute_ambitus(glyph.notes.notes, current_note_index);
  // gregorio_assert((ambitus2 = compute_ambitus(current_note)),
  //   compute_glyph_name, "unexpected unison on multi-note glyph",
  //     return "");
  // current_note = current_note -> next;
  if (glyph.notes.notes.length == 3) {
    // gregorio_snprintf(buf, BUFSIZE, "%s%s%s%s%s%s%s", fuse_head, shape,
    //   tex_ambitus[ambitus1], tex_ambitus[ambitus2],
    //   tex_ambitus[fuse_ambitus], liquescentia, fuse_tail);

    // eslint-disable-next-line max-len
    const name = `${fuse_head && FUSE[fuse_head] || ''}${SHAPE[shape]}${tex_ambitus[ambitus1]}${tex_ambitus[ambitus2]}${tex_ambitus[fuse_ambitus]}${liquescentia != null && LIQ[liquescentia] || ''}${fuse_tail != null && FUSE[fuse_tail] || ''}`;
    return name;
  }

  current_note_index++;
  const ambitus3 = compute_ambitus(glyph.notes.notes, current_note_index);
  // eslint-disable-next-line max-len
  const name =  `${fuse_head && FUSE[fuse_head] || ''}${SHAPE[shape]}${tex_ambitus[ambitus1]}${tex_ambitus[ambitus2]}${tex_ambitus[ambitus3]}${tex_ambitus[fuse_ambitus]}${liquescentia != null && LIQ[liquescentia] || ''}${fuse_tail || ''}`;
  return name;
  // gregorio_assert((ambitus3 = compute_ambitus(current_note)),
  //   compute_glyph_name, "unexpected unison on multi-note glyph",
  //     return "");
  // gregorio_snprintf(buf, BUFSIZE, "%s%s%s%s%s%s%s%s", fuse_head, shape,
  //   tex_ambitus[ambitus1], tex_ambitus[ambitus2], tex_ambitus[ambitus3],
  //   tex_ambitus[fuse_ambitus], liquescentia, fuse_tail);
}


enum FUSE {
  Lower,
  LowerOblatus,
  Upper,
  UpperOblatus,
  Down,
  Up,
}

enum LIQ {
  Deminutus,
  Ascendens,
  Descendens,
  InitioDebilis,
  InitioDebilisDeminutus,
  InitioDebilisAscendens,
  InitioDebilisDescendens,
  Nothing
}

export enum SHAPE {
  Ancus,
  AncusLongqueue,
  AscendensOriscus,
  AscendensOriscusLineBL,
  AscendensOriscusLineTL,
  AscendensOriscusScapus,
  AscendensOriscusScapusLongqueue,
  AscendensOriscusScapusOpenqueue,
  AscendensPunctumInclinatum,
  DescendensOriscus,
  DescendensOriscusLineBL,
  DescendensOriscusLineTL,
  DescendensOriscusScapus,
  DescendensOriscusScapusLongqueue,
  DescendensOriscusScapusOpenqueue,
  DescendensPunctumInclinatum,
  Flat,
  FlatParen,
  Flexus,
  FlexusLongqueue,
  FlexusNobar,
  FlexusOpenqueue,
  FlexusOriscus,
  FlexusOriscusInusitatus,
  FlexusOriscusScapus,
  FlexusOriscusScapusInusitatus,
  FlexusOriscusScapusInusitatusLongqueue,
  FlexusOriscusScapusInusitatusOpenqueue,
  FlexusOriscusScapusLongqueue,
  FlexusOriscusScapusOpenqueue,
  Linea,
  LineaPunctum,
  Natural,
  NaturalParen,
  OriscusDeminutus,
  Pes,
  PesAscendensOriscus,
  PesDescendensOriscus,
  PesQuadratum,
  PesQuadratumLongqueue,
  PesQuadratumOpenqueue,
  PesQuassus,
  PesQuassusInusitatus,
  PesQuassusInusitatusLongqueue,
  PesQuassusInusitatusOpenqueue,
  PesQuassusLongqueue,
  PesQuassusOpenqueue,
  Porrectus,
  PorrectusFlexus,
  PorrectusFlexusLongqueue,
  PorrectusFlexusNobar,
  PorrectusLongqueue,
  PorrectusNobar,
  Punctum,
  PunctumAscendens,
  PunctumDeminutus,
  PunctumDescendens,
  PunctumInclinatumAuctus,
  PunctumInclinatumDeminutus,
  PunctumLineBL,
  PunctumLineTL,
  Quilisma,
  QuilismaPes,
  QuilismaPesQuadratum,
  QuilismaPesQuadratumLongqueue,
  QuilismaPesQuadratumOpenqueue,
  StansPunctumInclinatum,
  Salicus,
  SalicusFlexus,
  SalicusLongqueue,
  Scandicus,
  Sharp,
  SharpParen,
  Stropha,
  StrophaAucta,
  StrophaAuctaLongtail,
  Torculus,
  TorculusLiquescens,
  TorculusLiquescensQuilisma,
  TorculusQuilisma,
  TorculusResupinus,
  TorculusResupinusQuilisma,
  Virga,
  VirgaLongqueue,
  VirgaOpenqueue,
  VirgaReversa,
  VirgaReversaAscendens,
  VirgaReversaDescendens,
  VirgaReversaLongqueue,
  VirgaReversaLongqueueAscendens,
  VirgaReversaLongqueueDescendens,
  VirgaReversaOpenqueue,
  VirgaReversaOpenqueueAscendens,
  VirgaReversaOpenqueueDescendens,
}

function gregoriotex_determine_liquescentia(type: gtex_glyph_liquescentia, glyph: GregorioGlyph): LIQ {
  let liquescentia = glyph.notes.liquescentia;

  switch (type) {
    case gtex_glyph_liquescentia.ALL:
      break;
    case gtex_glyph_liquescentia.NO_INITIO:
      liquescentia &= ~GregorioLiquescentia.INITIO_DEBILIS;
      break;
    case gtex_glyph_liquescentia.ONLY_DEMINUTUS:
      liquescentia &= GregorioLiquescentia.INITIO_DEBILIS | GregorioLiquescentia.DEMINUTUS;
      break;
    case gtex_glyph_liquescentia.FUSIBLE_INITIO:
      if (glyph.notes.fuse_to_next_glyph) {
        liquescentia &= GregorioLiquescentia.INITIO_DEBILIS;
        break;
      }
    /* fall through */
    case gtex_glyph_liquescentia.NONE:
      liquescentia = GregorioLiquescentia.NO_LIQUESCENTIA;
      break;
  }

  /* now we convert liquescentia into the good GregorioTeX liquescentia
 numbers */

  switch (liquescentia & ~GregorioLiquescentia.FUSED) {
    case GregorioLiquescentia.DEMINUTUS:
      return LIQ.Deminutus;
    case GregorioLiquescentia.AUCTUS_ASCENDENS:
      return LIQ.Ascendens;
    case GregorioLiquescentia.AUCTUS_DESCENDENS:
      return LIQ.Descendens;
    case GregorioLiquescentia.INITIO_DEBILIS:
      return LIQ.InitioDebilis;
    case GregorioLiquescentia.DEMINUTUS_INITIO_DEBILIS:
      return LIQ.InitioDebilisDeminutus;
    case GregorioLiquescentia.AUCTUS_ASCENDENS_INITIO_DEBILIS:
      return LIQ.InitioDebilisAscendens;
    case GregorioLiquescentia.AUCTUS_DESCENDENS_INITIO_DEBILIS:
      return LIQ.InitioDebilisDescendens;
    case GregorioLiquescentia.NO_LIQUESCENTIA:
      /* break out and return "Nothing" */
      break;
  }

  return LIQ.Nothing;
}




const tex_ambitus = [
  '', 'One', 'Two', 'Three', 'Four', 'Five'
];

function compute_ambitus(notes: GregorioNoteElement[], current_note_index: number): number {
  const first = notes[current_note_index].pitch;
  const second = notes[current_note_index + 1].pitch;
  let ambitus: number;
  if (first < second) {
    ambitus = second - first;
  } else {
    ambitus = first - second;
  }
  // gregorio_assert2(ambitus >= 1 && ambitus <= MAX_AMBITUS, compute_ambitus,
  //   "unsupported ambitus: %d", ambitus, return 0);
  return ambitus;
}


function quadratum_shape(glyph: GregorioGlyph,
  orig_base_shape: number, longqueue_shape: number,
  openqueue_shape: number): number {
  const base_shape = orig_base_shape;

  const ambitus_one: boolean = (glyph.notes.notes[1].pitch - glyph.notes.notes[0].pitch == 1);

  if (!is_tail_liquescentia(glyph.notes.liquescentia)) {
    switch (adjusted_queuetype_of(glyph.notes.notes[0], glyph.notes.notes[1], 0)) {
      case queuetype.Q_ON_SPACE_BELOW_BOTTOM_LINE:
        if (ambitus_one) {
          return openqueue_shape;
        }
      /* else fall through */
      case queuetype.Q_ON_SPACE_ABOVE_BOTTOM_LINE:
        /* at ambitus one, long and short are swapped becuase the queue
         * where the first note is on a space is longer than on a line */
        return ambitus_one ? longqueue_shape : base_shape;
      case queuetype.Q_ON_BOTTOM_LINE:
        if (ambitus_one) {
          return openqueue_shape;
        }
      /* else fall through */
      case queuetype.Q_ON_LINE_ABOVE_BOTTOM_LINE:
        /* at ambitus one, long and short are swapped becuase the queue
         * where the first note is on a line is shorter than on a space */
        return ambitus_one ? base_shape : longqueue_shape;
    }
    /* not reachable unless there's a programming error */
    /* LCOV_EXCL_START */
    // gregorio_fail(quadratum_shape, "unexpected queue length");
    /* fall out of the conditional */
  }
  /* LCOV_EXCL_STOP */
  return base_shape;
}

export enum queuetype {
  Q_ON_SPACE_BELOW_BOTTOM_LINE,
  Q_ON_SPACE_ABOVE_BOTTOM_LINE,
  Q_ON_BOTTOM_LINE,
  Q_ON_LINE_ABOVE_BOTTOM_LINE
} queuetype;

export function adjusted_queuetype_of(note: GregorioNoteElement,
  queue_note: GregorioNoteElement, adjustment: number): queuetype {
  switch (note.pitch + adjustment - LOWEST_PITCH) {
    case 0:
      return queuetype.Q_ON_SPACE_BELOW_BOTTOM_LINE;
    case 1:
      return queuetype.Q_ON_BOTTOM_LINE;
    case 2:
      return queue_note.low_ledger_line ?
        queuetype.Q_ON_SPACE_ABOVE_BOTTOM_LINE : queuetype.Q_ON_SPACE_BELOW_BOTTOM_LINE;
    case 3:
      return queue_note.low_ledger_line ?
        queuetype.Q_ON_LINE_ABOVE_BOTTOM_LINE : queuetype.Q_ON_BOTTOM_LINE;
    case 5:
    case 7:
    case 9:
    case 11:
    case 13:
      return queuetype.Q_ON_LINE_ABOVE_BOTTOM_LINE;
    default:
      return queuetype.Q_ON_SPACE_ABOVE_BOTTOM_LINE;
  }
}

function flexus_shape(glyph: GregorioGlyph,
  ambitus: number, base_shape: SHAPE,
  longqueue_shape: SHAPE, openqueue_shape: SHAPE): SHAPE {
  const ambitus_one = (ambitus == 1);
  switch (adjusted_queuetype_of(glyph.notes.notes[1], glyph.notes.notes[0], 0)) {
    case queuetype.Q_ON_SPACE_BELOW_BOTTOM_LINE:
      if (ambitus_one) {
        return openqueue_shape;
      }
    /* else fall through */
    case queuetype.Q_ON_SPACE_ABOVE_BOTTOM_LINE:
      /* at ambitus one, long and short are swapped becuase the queue where
       * the second note is on a space is longer than on a line */
      return ambitus_one ? longqueue_shape : base_shape;
    case queuetype.Q_ON_BOTTOM_LINE:
      if (ambitus_one) {
        return openqueue_shape;
      }
    /* else fall through */
    case queuetype.Q_ON_LINE_ABOVE_BOTTOM_LINE:
      /* at ambitus one, long and short are swapped becuase the queue where
       * the second note is on a line is shorter than on a space */
      return ambitus_one ? base_shape : longqueue_shape;
  }
  /* not reachable unless there's a programming error */
  /* LCOV_EXCL_START */
  // gregorio_fail(flexus_shape, "unexpected queue length");
  // return base_shape;
  /* LCOV_EXCL_STOP */
}

function porrectus_shape(glyph: GregorioGlyph,
  base_shape: SHAPE, longqueue_shape: SHAPE): SHAPE {
  const first_note = glyph.notes.notes[0];
  if ((glyph.notes.glyph_type == GregorioGlyphType.PORRECTUS
    && (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS))
    || first_note.pitch - glyph.notes.notes[1].pitch == 1) {
    switch (queuetype_of(first_note)) {
      case queuetype.Q_ON_SPACE_BELOW_BOTTOM_LINE:
      case queuetype.Q_ON_SPACE_ABOVE_BOTTOM_LINE:
      case queuetype.Q_ON_BOTTOM_LINE:
        return base_shape;
      case queuetype.Q_ON_LINE_ABOVE_BOTTOM_LINE:
        return longqueue_shape;
    }
  } /* must be optimized out; LCOV_EXCL_LINE */
  return base_shape;
}

export function queuetype_of(note: GregorioNoteElement): queuetype {
  return adjusted_queuetype_of(note, note, 0);
}

export function determine_leading_shape(glyph: GregorioGlyph): string {
  const buf: string[] = [];
  const ambitus = compute_ambitus(glyph.notes.notes, 0);
  let head: string;
  let head_liquescence: string;

  switch (glyph.notes.notes[0].shape) {
    case GregorioShape.QUILISMA:
      head = 'Quilisma';
      break;
    case GregorioShape.ORISCUS_ASCENDENS:
    case GregorioShape.ORISCUS_DESCENDENS:
      head = 'Oriscus';
      break;
    default:
      head = 'Punctum';
      break;
  }

  if (glyph.notes.liquescentia & GregorioLiquescentia.INITIO_DEBILIS) {
    head_liquescence = 'InitioDebilis';
  } else {
    head_liquescence = '';
  }

  buf.push('Leading');
  buf.push(head);
  buf.push(tex_ambitus[ambitus]);
  buf.push(head_liquescence);

  return buf.join('');
}
